<template>
  <div class="pa-4">
    <!-- Header -->
    <div class="d-flex align-center mb-6">
      <v-btn icon="mdi-arrow-left" variant="text" @click="$router.back()" class="mr-2"></v-btn>
      <div>
        <h1 class="text-h5 font-weight-black">Studio Directory</h1>
        <p class="text-caption text-grey mt-1">Manage Indonesian game developer studios</p>
      </div>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        variant="flat"
        prepend-icon="mdi-plus"
        class="rounded-pill font-weight-bold"
        @click="openCreate"
      >
        Add Studio
      </v-btn>
    </div>

    <!-- Access denied -->
    <v-alert
      v-if="!isAdminUser"
      type="error"
      variant="tonal"
      class="rounded-xl border mb-4"
    >
      Access denied. This page is only accessible to the admin account.
    </v-alert>

    <template v-else>
      <!-- Loading -->
      <div v-if="loading" class="text-center py-12">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <p class="mt-4 text-grey">Loading studios...</p>
      </div>

      <!-- Error -->
      <v-alert v-else-if="error" type="error" variant="tonal" class="rounded-xl border mb-4">
        {{ error }}
      </v-alert>

      <!-- Empty -->
      <div
        v-else-if="studios.length === 0"
        class="text-center py-12 border rounded-xl border-dashed"
        style="border-color: rgba(255,255,255,0.1) !important;"
      >
        <v-icon icon="mdi-domain" size="64" color="grey-darken-2" class="mb-4"></v-icon>
        <h3 class="text-h6 text-grey">No studios yet</h3>
        <p class="text-body-2 text-grey-darken-1 mt-2">Add the first Indonesian gamedev studio.</p>
      </div>

      <!-- Studio list -->
      <div v-else class="d-flex flex-column ga-3">
        <v-card
          v-for="studio in studios"
          :key="studio.rkey"
          class="rounded-xl border pa-4"
          style="background: rgba(255,255,255,0.02); border-color: rgba(255,255,255,0.08) !important;"
          flat
        >
          <div class="d-flex align-start ga-4">
            <v-avatar size="52" rounded="lg" color="surface-variant" class="border" style="border-color: rgba(255,255,255,0.1) !important; flex-shrink:0;">
              <v-img v-if="getLogoUrl(studio)" :src="getLogoUrl(studio)"></v-img>
              <v-icon v-else icon="mdi-domain" color="grey"></v-icon>
            </v-avatar>

            <div class="flex-grow-1 min-w-0">
              <div class="d-flex align-center flex-wrap ga-2 mb-1">
                <span class="text-subtitle-1 font-weight-black">{{ studio.name }}</span>
                <v-chip v-if="studio.location" size="x-small" color="grey" variant="tonal" prepend-icon="mdi-map-marker">
                  {{ studio.location }}
                </v-chip>
                <v-chip v-if="studio.foundedYear" size="x-small" color="grey" variant="tonal" prepend-icon="mdi-calendar">
                  {{ studio.foundedYear }}
                </v-chip>
              </div>

              <div v-if="studio.handle" class="text-caption text-primary mb-1">@{{ studio.handle }}</div>
              <div v-if="studio.description" class="text-body-2 text-grey-lighten-1 mb-2" style="white-space: pre-wrap;">{{ studio.description }}</div>

              <div class="d-flex flex-wrap ga-1 mb-2">
                <v-chip v-for="tag in studio.tags" :key="tag" size="x-small" color="primary" variant="tonal">
                  {{ tag }}
                </v-chip>
              </div>

              <a
                v-if="studio.website"
                :href="studio.website"
                target="_blank"
                rel="noopener"
                class="text-caption text-grey-lighten-1 text-decoration-none d-inline-flex align-center ga-1"
                style="opacity: 0.7;"
              >
                <v-icon icon="mdi-open-in-new" size="12"></v-icon>
                {{ studio.website }}
              </a>
            </div>

            <div class="d-flex flex-column ga-1" style="flex-shrink: 0;">
              <v-btn icon="mdi-pencil" size="small" variant="text" color="grey" @click="openEdit(studio)"></v-btn>
              <v-btn icon="mdi-delete" size="small" variant="text" color="error" @click="confirmDelete(studio)"></v-btn>
            </div>
          </div>
        </v-card>
      </div>
    </template>

    <!-- Create / Edit Dialog -->
    <v-dialog v-model="showForm" max-width="600px" scrollable>
      <v-card class="rounded-xl bg-surface border" style="border-color: rgba(255,255,255,0.08) !important;">
        <v-card-title class="font-weight-black pa-5 pb-0 d-flex align-center justify-space-between">
          {{ editingStudio ? 'Edit Studio' : 'Add Studio' }}
          <v-btn icon="mdi-close" variant="text" size="small" @click="showForm = false"></v-btn>
        </v-card-title>

        <v-card-text class="pa-5">
          <v-form ref="formRef" @submit.prevent="submitForm">

            <!-- Logo upload -->
            <div class="mb-5">
              <div class="text-caption text-grey mb-2">Studio Logo</div>
              <div class="d-flex align-center ga-4">
                <v-avatar
                  size="72"
                  rounded="lg"
                  color="surface-variant"
                  class="border cursor-pointer"
                  style="border-color: rgba(255,255,255,0.15) !important;"
                  @click="triggerLogoInput"
                >
                  <v-img v-if="logoPreview" :src="logoPreview"></v-img>
                  <v-icon v-else icon="mdi-domain" color="grey" size="32"></v-icon>
                </v-avatar>
                <div>
                  <v-btn
                    variant="tonal"
                    color="grey"
                    size="small"
                    prepend-icon="mdi-upload"
                    class="rounded-pill mb-1"
                    @click="triggerLogoInput"
                  >
                    Upload Logo
                  </v-btn>
                  <div class="text-caption text-grey" style="opacity: 0.6;">PNG, JPG or WebP. Max 1 MB.</div>
                  <v-btn
                    v-if="logoPreview && pendingLogoFile"
                    variant="text"
                    color="error"
                    size="x-small"
                    class="mt-1 px-0"
                    @click="clearLogo"
                  >
                    Remove
                  </v-btn>
                </div>
              </div>
              <input
                ref="logoInputRef"
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                class="d-none"
                @change="onLogoFileChange"
              />
            </div>

            <v-text-field
              v-model="form.name"
              label="Studio Name *"
              variant="outlined"
              density="comfortable"
              class="mb-3 rounded-lg"
              :rules="[v => !!v || 'Name is required']"
            ></v-text-field>

            <v-text-field
              v-model="form.handle"
              label="Bluesky Handle"
              variant="outlined"
              density="comfortable"
              class="mb-3 rounded-lg"
              placeholder="e.g. mystudio.bsky.social"
              hint="Without the @ symbol"
              persistent-hint
            ></v-text-field>

            <v-text-field
              v-model="form.website"
              label="Website"
              variant="outlined"
              density="comfortable"
              class="mb-3 rounded-lg"
              placeholder="https://..."
            ></v-text-field>

            <v-text-field
              v-model="form.location"
              label="Location"
              variant="outlined"
              density="comfortable"
              class="mb-3 rounded-lg"
              placeholder="e.g. Jakarta, Bandung"
            ></v-text-field>

            <v-text-field
              v-model="form.foundedYear"
              label="Founded Year"
              variant="outlined"
              density="comfortable"
              class="mb-3 rounded-lg"
              placeholder="e.g. 2018"
            ></v-text-field>

            <v-textarea
              v-model="form.description"
              label="Description"
              variant="outlined"
              density="comfortable"
              class="mb-3 rounded-lg"
              rows="3"
              auto-grow
            ></v-textarea>

            <v-text-field
              v-model="tagsInput"
              label="Tags"
              variant="outlined"
              density="comfortable"
              class="mb-3 rounded-lg"
              placeholder="e.g. indie, mobile, pc"
              hint="Separate tags with commas"
              persistent-hint
            ></v-text-field>
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-5 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" class="font-weight-bold" @click="showForm = false">Cancel</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            class="rounded-pill font-weight-bold px-6"
            :loading="saving"
            @click="submitForm"
          >
            {{ editingStudio ? 'Save Changes' : 'Add Studio' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirmation -->
    <v-dialog v-model="showDeleteConfirm" max-width="420px">
      <v-card class="rounded-xl bg-surface border" style="border-color: rgba(255,255,255,0.08) !important;">
        <v-card-title class="text-h6 font-weight-black pa-6">Delete Studio?</v-card-title>
        <v-card-text class="pa-6 pt-0 text-grey-lighten-1">
          Are you sure you want to delete <strong>{{ studioToDelete?.name }}</strong>? This cannot be undone.
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" class="font-weight-bold" @click="showDeleteConfirm = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" class="rounded-pill font-weight-bold px-6" :loading="deleting" @click="doDelete">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" rounded="pill" location="bottom">
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import {
  currentUser,
  isAdmin,
  getStudios,
  createStudio,
  updateStudio,
  deleteStudio,
  uploadBlob,
  resolveStudioLogoUrl,
  getAdminDid,
  type Studio
} from '@/services/bluesky';

const isAdminUser = computed(() => isAdmin());

const studios = ref<Studio[]>([]);
const loading = ref(true);
const error = ref('');
const adminDid = ref('');

const showForm = ref(false);
const editingStudio = ref<Studio | null>(null);
const saving = ref(false);
const formRef = ref<any>(null);

// Logo upload state
const logoInputRef = ref<HTMLInputElement | null>(null);
const pendingLogoFile = ref<File | null>(null);
const logoPreview = ref('');
const existingLogoBlob = ref<any>(null);

const showDeleteConfirm = ref(false);
const studioToDelete = ref<Studio | null>(null);
const deleting = ref(false);

const snackbar = ref({ show: false, text: '', color: 'success' });

const emptyForm = () => ({
  name: '',
  handle: '',
  website: '',
  location: '',
  foundedYear: '',
  description: '',
  tags: [] as string[]
});

const form = ref(emptyForm());
const tagsInput = ref('');

const notify = (text: string, color = 'success') => {
  snackbar.value = { show: true, text, color };
};

const getLogoUrl = (studio: Studio) => resolveStudioLogoUrl(studio, adminDid.value);

const loadStudios = async () => {
  loading.value = true;
  error.value = '';
  try {
    studios.value = await getStudios();
  } catch (e: any) {
    error.value = e?.message || 'Failed to load studios';
  } finally {
    loading.value = false;
  }
};

const triggerLogoInput = () => logoInputRef.value?.click();

const onLogoFileChange = (e: Event) => {
  const file = (e.target as HTMLInputElement).files?.[0];
  if (!file) return;
  if (file.size > 1024 * 1024) {
    notify('Logo must be under 1 MB', 'error');
    return;
  }
  pendingLogoFile.value = file;
  logoPreview.value = URL.createObjectURL(file);
};

const clearLogo = () => {
  pendingLogoFile.value = null;
  logoPreview.value = existingLogoBlob.value
    ? resolveStudioLogoUrl({ name: '', createdAt: '', logoBlob: existingLogoBlob.value }, adminDid.value)
    : '';
  if (logoInputRef.value) logoInputRef.value.value = '';
};

const openCreate = () => {
  editingStudio.value = null;
  form.value = emptyForm();
  tagsInput.value = '';
  pendingLogoFile.value = null;
  logoPreview.value = '';
  existingLogoBlob.value = null;
  showForm.value = true;
};

const openEdit = (studio: Studio) => {
  editingStudio.value = studio;
  form.value = {
    name: studio.name,
    handle: studio.handle || '',
    website: studio.website || '',
    location: studio.location || '',
    foundedYear: studio.foundedYear || '',
    description: studio.description || '',
    tags: studio.tags || []
  };
  tagsInput.value = (studio.tags || []).join(', ');
  pendingLogoFile.value = null;
  existingLogoBlob.value = studio.logoBlob || null;
  logoPreview.value = getLogoUrl(studio);
  showForm.value = true;
};

const submitForm = async () => {
  const { valid } = await formRef.value?.validate();
  if (!valid) return;

  saving.value = true;
  try {
    const tags = tagsInput.value.split(',').map(t => t.trim()).filter(Boolean);

    // Upload new logo blob if a file was picked
    let logoBlob = existingLogoBlob.value || undefined;
    if (pendingLogoFile.value) {
      logoBlob = await uploadBlob(pendingLogoFile.value, pendingLogoFile.value.type);
    }

    const payload: any = { ...form.value, tags };
    if (logoBlob) payload.logoBlob = logoBlob;

    if (editingStudio.value?.rkey) {
      await updateStudio(editingStudio.value.rkey, payload);
      notify('Studio updated successfully');
    } else {
      await createStudio(payload);
      notify('Studio added successfully');
    }

    showForm.value = false;
    await loadStudios();
  } catch (e: any) {
    notify(e?.message || 'Failed to save studio', 'error');
  } finally {
    saving.value = false;
  }
};

const confirmDelete = (studio: Studio) => {
  studioToDelete.value = studio;
  showDeleteConfirm.value = true;
};

const doDelete = async () => {
  if (!studioToDelete.value?.rkey) return;
  deleting.value = true;
  try {
    await deleteStudio(studioToDelete.value.rkey);
    notify('Studio deleted');
    showDeleteConfirm.value = false;
    await loadStudios();
  } catch (e: any) {
    notify(e?.message || 'Failed to delete studio', 'error');
  } finally {
    deleting.value = false;
  }
};

onMounted(async () => {
  adminDid.value = await getAdminDid();
  if (isAdminUser.value) loadStudios();
  else loading.value = false;
});
</script>
